import { Observable, TimeoutError, retry, throwError, timeout, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';

import { CORE_CONFIG } from '../../config/core-config';
import { NetworkService } from '../../network/network.service';

/** Per-request timeout override (ms). Default: 25 000 ms. */
export const HTTP_TIMEOUT = new HttpContextToken<number>(() => 25_000);

const IDEMPOTENT_METHODS = ['GET', 'HEAD', 'OPTIONS'];

/** Fallas transitorias: 0 = red caída o timeout local (ver catchError de abajo). */
const RETRYABLE_STATUSES = [0, 408, 425, 429];

/**
 * Un 4xx es determinista: reintentarlo triplica el tráfico y siempre trae la misma
 * respuesta. Se vio al cambiar de campus, con `bustickets/{id}` devolviendo tres 404
 * seguidos para un boleto del campus anterior.
 */
const isRetryable = (error: unknown): boolean =>
  !(error instanceof HttpErrorResponse) || RETRYABLE_STATUSES.includes(error.status) || error.status >= 500;

export const timeoutInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const config = inject(CORE_CONFIG);
  if (!request.url.includes(config.apiBaseUrl)) {
    return next(request);
  }

  const network = inject(NetworkService);
  const timeoutMs = request.context.get(HTTP_TIMEOUT);
  const isIdempotent = IDEMPOTENT_METHODS.includes(request.method);

  let source$ = next(request).pipe(
    timeout(timeoutMs),
    catchError((error: unknown) => {
      if (error instanceof TimeoutError) {
        const requestId = request.headers.get('X-Request-ID') || 'unknown';

        console.error(
          `[HTTP Timeout] ${request.method} ${request.url} (X-Request-ID: ${requestId}) timed out after ${timeoutMs}ms`
        );

        return throwError(
          () =>
            new HttpErrorResponse({
              error,
              status: 0,
              statusText: `Request Timeout (${timeoutMs}ms)`,
              url: request.url,
            })
        );
      }
      return throwError(() => error);
    })
  );

  // Retry idempotent requests with exponential backoff (2s, 4s).
  // Each retry re-subscribes to the full inner chain (auth, timeout, etc.)
  if (isIdempotent) {
    source$ = source$.pipe(
      retry({
        count: 2,
        delay: (error, attempt) => {
          if (!network.isOnline()) {
            return throwError(() => error);
          }
          // Cubre también el 401: no es reintentable, lo maneja errorInterceptor.
          if (!isRetryable(error)) {
            return throwError(() => error);
          }
          console.warn(`[HTTP Retry] ${request.method} ${request.url} — attempt ${attempt + 1}`);
          return timer(attempt * 2000);
        },
      })
    );
  }

  return source$;
};
